import React, { useState, useEffect, useCallback, useMemo } from 'react';
// jspdf is lazy-loaded inside the payroll PDF download handler below
// to keep it off the initial bundle (~200KB gzip).
import { fetchWithAuth } from '../../utils/api';
import RowsPerPageSelector from '../common/RowsPerPageSelector';
import { 
  PlusCircle, 
  Edit, 
  X,
  UserSquare2,
  DollarSign,
  History
} from 'lucide-react';
import Pagination from '../common/Pagination';
import SortSelector from '../common/SortSelector';
import ConfirmationModal from '../modals/ConfirmationModal';

function Employees({ user, onEmployeeLinkClick, handleLogout, targetEmployeeId, clearTargetEmployee }) {
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [transactionEmployee, setTransactionEmployee] = useState(null);
  const [viewTransactionsEmployee, setViewTransactionsEmployee] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [viewImage, setViewImage] = useState(null);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [layoutMode, setLayoutMode] = useState(window.innerWidth < 768 ? 'cards' : 'table');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'first_name', direction: 'asc' });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter States
  const [filters, setFilters] = useState({
    name: '',
    position: '',
    location: '',
    salary: { op: 'eq', val: '' }
  });
  
  const [newEmployee, setNewEmployee] = useState({
    first_name: '',
    last_name: '',
    position: '',
    current_salary: '',
    current_balance: '',
    location_id: '',
    image: ''
  });

  const [transaction, setTransaction] = useState({
    amount: '',
    type: 'Wage'
  });

  // Each fetcher accepts an optional AbortSignal so the mount-effect can
  // cancel in-flight requests on unmount / dep change.
  const fetchEmployees = useCallback((signal) => {
    return fetchWithAuth('/employees', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setEmployees(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  const fetchLocations = useCallback((signal) => {
    return fetchWithAuth('/locations', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setLocations(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    Promise.allSettled([
      fetchEmployees(controller.signal),
      fetchLocations(controller.signal),
    ]).then(() => setIsLoading(false));
    return () => controller.abort();
  }, [fetchEmployees, fetchLocations]);

  useEffect(() => {
    if (targetEmployeeId && employees.length > 0) {
      const target = employees.find(e => e.id === targetEmployeeId);
      if (target) {
        setViewingEmployee(target);
      }
      if (clearTargetEmployee) clearTargetEmployee(null);
    }
  }, [targetEmployeeId, employees, clearTargetEmployee]);

  const handleInputChange = (e, isEditing = false) => {
    const { name, value } = e.target;
    if (isEditing) {
      setEditingEmployee({ ...editingEmployee, [name]: value });
    } else {
      setNewEmployee({ ...newEmployee, [name]: value });
    }
  };

  const handleFileChange = (e, isEditing = false) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEditing) {
          setEditingEmployee({ ...editingEmployee, image: reader.result });
        } else {
          setNewEmployee({ ...newEmployee, image: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFilterChange = (field, value, subField = null) => {
    if (subField) {
      setFilters(prev => ({ ...prev, [field]: { ...prev[field], [subField]: value } }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  // Reset to first page when filters, rows per page or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, rowsPerPage, sortConfig]);

  // Filter Logic
  const filteredEmployees = useMemo(() => {
    return employees.filter(e => {
      // Name
      if (filters.name) {
        const fullName = `${e.first_name} ${e.last_name}`.toLowerCase();
        if (!fullName.includes(filters.name.toLowerCase())) return false;
      }

      // Position
      if (filters.position && !e.position.toLowerCase().includes(filters.position.toLowerCase())) return false;

      // Location
      if (filters.location && (!e.location_name || !e.location_name.toLowerCase().includes(filters.location.toLowerCase()))) return false;

      // Salary
      if (filters.salary.val !== '') {
        const salaryVal = parseFloat(filters.salary.val);
        const empSalary = e.current_salary;
        if (filters.salary.op === 'gt' && empSalary <= salaryVal) return false;
        if (filters.salary.op === 'lt' && empSalary >= salaryVal) return false;
        if (filters.salary.op === 'eq' && empSalary !== salaryVal) return false;
      }

      return true;
    });
  }, [employees, filters]);

  // Sorting Logic
  const sortedEmployees = useMemo(() => {
    let sortableItems = [...filteredEmployees];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle special cases
        if (sortConfig.key === 'name') {
          aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
          bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
        } else if (sortConfig.key === 'location') {
          aVal = (a.location_name || '').toLowerCase();
          bVal = (b.location_name || '').toLowerCase();
        } else if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredEmployees, sortConfig]);

  const paginatedEmployees = useMemo(() => {
    if (rowsPerPage === 'all') return sortedEmployees;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedEmployees.slice(start, start + rowsPerPage);
  }, [sortedEmployees, currentPage, rowsPerPage]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);
    fetchWithAuth('/employees/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...newEmployee,
        current_salary: parseFloat(newEmployee.current_salary) || 0,
        current_balance: parseFloat(newEmployee.current_balance) || 0,
        location_id: newEmployee.location_id ? parseInt(newEmployee.location_id) : 0
      })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    })
    .then(() => {
      fetchEmployees();
      setNewEmployee({ first_name: '', last_name: '', position: '', current_salary: '', current_balance: '', location_id: '', image: '' });
      setShowRecordModal(false);
      setError('');
    })
    .catch(err => setError(err.message))
    .finally(() => setIsSubmitting(false));
  };

  const handleViewClick = (employee) => {
    setViewingEmployee(employee);
    setError('');
    setEditingEmployee(null);
    setTransactionEmployee(null);
    setViewTransactionsEmployee(null);
  };

  const handleEditClick = (employee) => {
    setEditingEmployee({ ...employee, location_id: employee.location_id || '' });
    setTransactionEmployee(null);
    setViewTransactionsEmployee(null);
    setViewingEmployee(null);
    setError('');
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);
    fetchWithAuth('/employees/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...editingEmployee,
        current_salary: parseFloat(editingEmployee.current_salary) || 0,
        current_balance: parseFloat(editingEmployee.current_balance) || 0,
        location_id: editingEmployee.location_id ? parseInt(editingEmployee.location_id) : 0
      })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchEmployees();
      setEditingEmployee(null);
    })
    .catch(err => setError(err.message))
    .finally(() => setIsSubmitting(false));
  };

  const handleTransactionClick = (employee) => {
    setTransactionEmployee(employee);
    setEditingEmployee(null);
    setViewTransactionsEmployee(null);
    setTransaction({ amount: '', type: 'Wage' });
    setError('');
  };

  const handleViewTransactionsClick = (employee) => {
    setViewTransactionsEmployee(employee);
    setEditingEmployee(null);
    setTransactionEmployee(null);
    setError('');
    fetchWithAuth(`/payroll/transactions?employee_id=${employee.id}`, {}, user, handleLogout)
      .then(res => res.json())
      .then(data => setTransactions(data || []));
  };

  const handleDelete = (e) => {
    setDeleteConfirm({ isOpen: true, id: e.id, name: `${e.first_name} ${e.last_name}` });
  };

  const confirmDelete = () => {
    const id = deleteConfirm.id;
    setError('');
    fetchWithAuth(`/employees/delete?id=${id}`, {
      method: 'POST'
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchEmployees();
      setDeleteConfirm({ isOpen: false, id: null, name: '' });
    })
    .catch(err => {
      setError(err.message);
      setDeleteConfirm({ isOpen: false, id: null, name: '' });
    });
  };

  const handleTransactionSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);
    fetchWithAuth('/payroll/transaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        employee_id: transactionEmployee.id,
        amount: parseFloat(transaction.amount) || 0,
        type: transaction.type
      })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchEmployees();
      setTransactionEmployee(null);
    })
    .catch(err => setError(err.message))
    .finally(() => setIsSubmitting(false));
  };

  const [transSortConfig, setTransSortConfig] = useState({ key: 'date', direction: 'desc' });

  const sortedTransactions = useMemo(() => {
    let sortableItems = [...transactions];
    if (transSortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[transSortConfig.key];
        let bVal = b[transSortConfig.key];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return transSortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return transSortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [transactions, transSortConfig]);

  const requestTransSort = (key) => {
    let direction = 'asc';
    if (transSortConfig.key === key && transSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTransSortConfig({ key, direction });
  };

  return (
    <div className="employees-view">
      <div className="view-header">
        <h2>Employees</h2>
        <div className="view-actions">
          <button className="secondary-btn no-print" onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button className="submit-btn no-print" onClick={() => setShowRecordModal(true)}>
            Add Employee
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="control-group">
          <RowsPerPageSelector value={rowsPerPage} onChange={setRowsPerPage} />
          {layoutMode === 'cards' && (
            <SortSelector 
              options={[
                { key: 'name', label: 'Name' },
                { key: 'position', label: 'Position' },
                { key: 'location', label: 'Location' },
                { key: 'current_salary', label: 'Salary' },
                { key: 'current_balance', label: 'Balance' }
              ]} 
              sortConfig={sortConfig} 
              onSortChange={setSortConfig} 
            />
          )}
        </div>
        <div className="layout-toggle no-print">
          <button className={layoutMode === 'table' ? 'active' : ''} onClick={() => setLayoutMode('table')}>Table</button>
          <button className={layoutMode === 'cards' ? 'active' : ''} onClick={() => setLayoutMode('cards')}>Cards</button>
        </div>
      </div>

      {showFilters && (
        <div className="filter-panel">
          <h4>Filters</h4>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Name</label>
              <input placeholder="Search name..." value={filters.name} onChange={(e) => handleFilterChange('name', e.target.value)} />
            </div>

            <div className="filter-group">
              <label>Position</label>
              <input placeholder="Search position..." value={filters.position} onChange={(e) => handleFilterChange('position', e.target.value)} />
            </div>

            <div className="filter-group">
              <label>Location</label>
              <input 
                list="emp-location-options"
                placeholder="Search location..." 
                value={filters.location} 
                onChange={(e) => handleFilterChange('location', e.target.value)} 
              />
              <datalist id="emp-location-options">
                {locations.map(l => <option key={l.id} value={l.name} />)}
              </datalist>
            </div>

            <div className="filter-group">
              <label>Salary</label>
              <div className="numeric-filter">
                <select value={filters.salary.op} onChange={(e) => handleFilterChange('salary', e.target.value, 'op')}>
                  <option value="eq">=</option>
                  <option value="gt">&gt;</option>
                  <option value="lt">&lt;</option>
                </select>
                <input type="number" placeholder="Value" value={filters.salary.val} onChange={(e) => handleFilterChange('salary', e.target.value, 'val')} />
              </div>
            </div>
          </div>
          <div className="filter-actions">
            <button onClick={() => setFilters({
              name: '', position: '', location: '',
              salary: { op: 'eq', val: '' }
            })} className="secondary-btn">Clear All Filters</button>
          </div>
        </div>
      )}

      {isLoading && <div className="loading-banner">Loading employees…</div>}

      {layoutMode === 'table' ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => requestSort('name')} className="sortable">Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('position')} className="sortable">Position {sortConfig.key === 'position' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('location')} className="sortable">Location {sortConfig.key === 'location' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('current_salary')} className="sortable text-right">Salary {sortConfig.key === 'current_salary' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('current_balance')} className="sortable text-right">Balance {sortConfig.key === 'current_balance' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th className="text-center">Photo</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEmployees.map(e => (
                <tr key={e.id} onClick={() => handleViewClick(e)} className="clickable-row">
                  <td>{e.first_name} {e.last_name}</td>
                  <td>{e.position}</td>
                  <td>{e.location_name || '-'}</td>
                  <td className="text-right">PHP {e.current_salary.toFixed(2)}</td>
                  <td className={`text-right ${e.current_balance < 0 ? 'status-error' : 'status-paid'}`}>PHP {e.current_balance.toFixed(2)}</td>
                  <td className="text-center">
                    {e.image ? (
                      <img 
                        src={e.image} 
                        alt={e.first_name} 
                        className="avatar-sm"
                        onClick={(event) => { event.stopPropagation(); setViewImage(e.image); }}
                      />
                    ) : '-'}
                  </td>
                  <td className="row-actions" onClick={(event) => event.stopPropagation()}>
                    <button className="action-btn sm" onClick={() => handleTransactionClick(e)}>Trans.</button>
                    <button className="action-btn sm" onClick={() => handleViewTransactionsClick(e)}>History</button>
                    <button className="action-btn sm" onClick={() => handleEditClick(e)}>Edit</button>
                    {user.role === 'admin' && <button className="cancel-btn sm" onClick={() => handleDelete(e)}>Del</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-grid">
          {paginatedEmployees.map(e => (
            <div key={e.id} className="data-card employee-card" onClick={() => handleViewClick(e)}>
              <div className="card-header">
                <div className="card-title-group">
                  {e.image && (
                    <img 
                      src={e.image} 
                      alt={e.first_name} 
                      className="avatar-md"
                    />
                  )}
                  <div className="card-title-stack">
                    <h4>{e.first_name} {e.last_name}</h4>
                    <span className="card-subtitle">{e.position}</span>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="card-item"><span className="label">Location</span><span className="value">{e.location_name || '-'}</span></div>
                <div className="card-item"><span className="label">Monthly Salary</span><span className="value">PHP {e.current_salary.toFixed(2)}</span></div>
                <div className="card-item"><span className="label">Current Balance</span><span className={`value ${e.current_balance < 0 ? 'status-error' : 'status-paid'}`}>PHP {e.current_balance.toFixed(2)}</span></div>
              </div>
              <div className="card-actions" onClick={e => e.stopPropagation()}>
                <button className="action-btn sm" onClick={() => handleTransactionClick(e)}>Trans.</button>
                <button className="action-btn sm" onClick={() => handleViewTransactionsClick(e)}>History</button>
                <button className="action-btn sm" onClick={() => handleEditClick(e)}>Edit</button>
                {user.role === 'admin' && <button className="cancel-btn sm" onClick={() => handleDelete(e)}>Del</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination 
        totalItems={filteredEmployees.length} 
        itemsPerPage={rowsPerPage} 
        currentPage={currentPage} 
        onPageChange={setCurrentPage} 
      />

      {showRecordModal && (
        <div className="modal" onClick={() => { setShowRecordModal(false); setError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <PlusCircle size={24} />
                <h3>Add New Employee</h3>
              </div>
              <button className="close-btn" onClick={() => setShowRecordModal(false)}><X size={20} /></button>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleCreateSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input name="first_name" value={newEmployee.first_name} onChange={handleInputChange} placeholder="John" required />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input name="last_name" value={newEmployee.last_name} onChange={handleInputChange} placeholder="Doe" required />
                </div>
              </div>
              <div className="form-group">
                <label>Position</label>
                <input name="position" value={newEmployee.position} onChange={handleInputChange} placeholder="e.g. Farm Manager" required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Monthly Salary (PHP)</label>
                  <input name="current_salary" type="number" step="0.01" value={newEmployee.current_salary} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Starting Balance (PHP)</label>
                  <input name="current_balance" type="number" step="0.01" value={newEmployee.current_balance} onChange={handleInputChange} required />
                </div>
              </div>
              <div className="form-group">
                <label>Location</label>
                <select name="location_id" value={newEmployee.location_id} onChange={handleInputChange} required>
                  <option value="">Select Location</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Profile Photo</label>
                <input type="file" accept="image/*" onChange={handleFileChange} />
              </div>
              {newEmployee.image && (
                <div className="image-preview circular">
                  <img src={newEmployee.image} alt="Preview" />
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Add Employee'}</button>
                <button type="button" className="secondary-btn" onClick={() => { setShowRecordModal(false); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingEmployee && (
        <div className="modal" onClick={() => { setEditingEmployee(null); setError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <Edit size={24} />
                <h3>Edit Employee</h3>
              </div>
              <button className="close-btn" onClick={() => setEditingEmployee(null)}><X size={20} /></button>
            </div>
            <div style={{marginBottom: '1.5rem', color: 'var(--google-grey-600)', fontSize: '0.9rem'}}>
              Updating profile for <strong>{editingEmployee.first_name} {editingEmployee.last_name}</strong>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleUpdateSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input name="first_name" value={editingEmployee.first_name} onChange={(e) => handleInputChange(e, true)} required />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input name="last_name" value={editingEmployee.last_name} onChange={(e) => handleInputChange(e, true)} required />
                </div>
              </div>
              <div className="form-group">
                <label>Position</label>
                <input name="position" value={editingEmployee.position} onChange={(e) => handleInputChange(e, true)} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Monthly Salary (PHP)</label>
                  <input name="current_salary" type="number" step="0.01" value={editingEmployee.current_salary} onChange={(e) => handleInputChange(e, true)} required />
                </div>
                <div className="form-group">
                  <label>Current Balance (Manual Adj)</label>
                  <input name="current_balance" type="number" step="0.01" value={editingEmployee.current_balance} onChange={(e) => handleInputChange(e, true)} required />
                </div>
              </div>
              <div className="form-group">
                <label>Location</label>
                <select name="location_id" value={editingEmployee.location_id} onChange={(e) => handleInputChange(e, true)} required>
                  <option value="">Select Location</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Profile Photo</label>
                <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, true)} />
              </div>
              {editingEmployee.image && (
                <div className="image-preview circular">
                  <img src={editingEmployee.image} alt="Preview" />
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Update Profile'}</button>
                <button type="button" className="secondary-btn" onClick={() => { setEditingEmployee(null); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewingEmployee && (
        <div className="modal" onClick={() => setViewingEmployee(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <UserSquare2 size={24} />
                <h3>Employee Profile</h3>
              </div>
              <button className="close-btn" onClick={() => setViewingEmployee(null)}><X size={20} /></button>
            </div>
            <div className="profile-detail-header">
              {viewingEmployee.image ? (
                <img 
                  src={viewingEmployee.image} 
                  alt={viewingEmployee.first_name} 
                  className="avatar-lg"
                />
              ) : (
                <div className="avatar-placeholder">
                  {(viewingEmployee.first_name || '?').charAt(0)}{(viewingEmployee.last_name || '?').charAt(0)}
                </div>
              )}
              <div className="profile-identity">
                <h3>{viewingEmployee.first_name} {viewingEmployee.last_name}</h3>
                <p className="subtitle">{viewingEmployee.position}</p>
              </div>
            </div>
            
            <div className="details-grid">
              <div className="details-column">
                <p><strong>Location:</strong> {viewingEmployee.location_name || '-'}</p>
                <p><strong>Date Started:</strong> {new Date(viewingEmployee.date_started).toLocaleDateString()}</p>
              </div>
              <div className="details-column">
                <p><strong>Monthly Salary:</strong> PHP {viewingEmployee.current_salary.toFixed(2)}</p>
                <p><strong>Current Balance:</strong> <span className={viewingEmployee.current_balance < 0 ? 'status-error' : 'status-paid'}>PHP {viewingEmployee.current_balance.toFixed(2)}</span></p>
              </div>
            </div>

            <div className="form-actions center">
              <button className="action-btn" onClick={() => onEmployeeLinkClick(`${viewingEmployee.first_name} ${viewingEmployee.last_name}`)}>View Harvest History</button>
              <button className="secondary-btn" onClick={() => setViewingEmployee(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {transactionEmployee && (
        <div className="modal" onClick={() => { setTransactionEmployee(null); setError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <DollarSign size={24} />
                <h3>New Transaction</h3>
              </div>
              <button className="close-btn" onClick={() => setTransactionEmployee(null)}><X size={20} /></button>
            </div>
            <div style={{marginBottom: '1.5rem', color: 'var(--google-grey-600)', fontSize: '0.9rem'}}>
              Recording wage or advance for <strong>{transactionEmployee.first_name} {transactionEmployee.last_name}</strong>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleTransactionSubmit}>
              <div className="form-group">
                <label>Transaction Type</label>
                <select value={transaction.type} onChange={e => setTransaction({...transaction, type: e.target.value})}>
                  <option value="Wage">Wage (Add to Balance)</option>
                  <option value="Advance">Advance (Subtract from Balance)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Amount (PHP)</label>
                <input type="number" step="0.01" value={transaction.amount} onChange={e => setTransaction({...transaction, amount: e.target.value})} required />
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Submit Transaction'}</button>
                <button type="button" className="secondary-btn" onClick={() => { setTransactionEmployee(null); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewTransactionsEmployee && (
        <div className="modal" onClick={() => setViewTransactionsEmployee(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <History size={24} />
                <h3>Payroll History</h3>
              </div>
              <button className="close-btn" onClick={() => setViewTransactionsEmployee(null)}><X size={20} /></button>
            </div>
            <div style={{marginBottom: '1.5rem', color: 'var(--google-grey-600)', fontSize: '0.9rem'}}>
              Transaction logs for <strong>{viewTransactionsEmployee.first_name} {viewTransactionsEmployee.last_name}</strong>
            </div>
            <div className="history-summary">
              <p>Current Balance: <span className={viewTransactionsEmployee.current_balance < 0 ? 'status-error' : 'status-paid'}>PHP {viewTransactionsEmployee.current_balance.toFixed(2)}</span></p>
              <button className="action-btn sm" onClick={async () => {
                const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
                  import('jspdf'),
                  import('jspdf-autotable'),
                ]);
                const doc = new jsPDF();
                doc.text(`Payroll History: ${viewTransactionsEmployee.first_name} ${viewTransactionsEmployee.last_name}`, 14, 15);
                doc.text(`Current Balance: PHP ${viewTransactionsEmployee.current_balance.toFixed(2)}`, 14, 22);
                const tableColumn = ["Date", "Type", "Amount"];
                const tableRows = sortedTransactions.map(t => [new Date(t.date).toLocaleDateString(), t.type, `${t.type === 'Wage' ? '+' : '-'}${t.amount.toFixed(2)}`]);
                autoTable(doc, { head: [tableColumn], body: tableRows, startY: 30 });
                doc.save(`payroll_${viewTransactionsEmployee.first_name}_${new Date().toISOString().split('T')[0]}.pdf`);
              }}>Download PDF</button>
            </div>
            
            <div className="table-container compact">
              <table>
                <thead>
                  <tr>
                    <th onClick={() => requestTransSort('date')} className="sortable">Date {transSortConfig.key === 'date' && (transSortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                    <th onClick={() => requestTransSort('type')} className="sortable">Type {transSortConfig.key === 'type' && (transSortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                    <th onClick={() => requestTransSort('amount')} className="sortable text-right">Amount {transSortConfig.key === 'amount' && (transSortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTransactions.length === 0 ? (
                    <tr><td colSpan="3" className="text-center">No transactions found.</td></tr>
                  ) : (
                    sortedTransactions.map(t => (
                      <tr key={t.id}>
                        <td>{new Date(t.date).toLocaleDateString()}</td>
                        <td>{t.type}</td>
                        <td className={`text-right ${t.type === 'Wage' ? 'positive-amount' : 'negative-amount'}`}>
                          {t.type === 'Wage' ? '+' : '-'}{t.amount.toFixed(2)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <button className="secondary-btn" onClick={() => setViewTransactionsEmployee(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {viewImage && (
        <div className="image-modal" onClick={() => setViewImage(null)}>
          <div className="image-modal-content" onClick={e => e.stopPropagation()}>
            <img src={viewImage} alt="Full Size" />
            <div className="modal-actions">
              <button className="secondary-btn" onClick={() => setViewImage(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal 
        isOpen={deleteConfirm.isOpen}
        title="Delete Employee"
        message={`Are you sure you want to delete ${deleteConfirm.name}? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
      />
    </div>
  );
}

export default Employees;
